import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Clock, User, Phone, Mail, Calendar, FileText, ArrowLeft, CheckCircle, Trash2, Settings, Plus, CalendarClock, Save, ArrowUp, ArrowDown, ChevronRight, ChevronDown, Sparkles, AlertTriangle, Check, MapPin, ListOrdered, HelpCircle, Repeat2 } from 'lucide-react';
import { Appointment, AppointmentSeriesListItem, AppointmentSeriesSummary, Service } from '../types';


import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import BookingStep1 from './booking/BookingStep1';
import LoadingSpinner from './LoadingSpinner';
import MarkDepositPaidModal from './MarkDepositPaidModal';
import CustomerAppointmentsHistoryModal from './CustomerAppointmentsHistoryModal';
import { getAppointmentStartIso, getTotalDuration as getTotalDurationHelper, isPendingDepositOutstanding, formatAppointmentStatusLabel, isDepositOutstanding, getDepositAmountToRecord } from '../utils/appointmentHelpers';
import { canViewFullCustomerPhone } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';

interface AppointmentDetailsModalProps {
  appointment: Appointment | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (appointment: Appointment) => void;
  onCancel?: (appointmentId: string, cancellationData: { noShow: boolean; cancellationNotes: string }) => void;
  onConfirm?: (appointmentId: string) => void;
  onReschedule?: (appointment: Appointment) => void;
  onStatusChange?: (appointment: Appointment) => void; // Callback when appointment status changes
  /** When set, choosing another visit from the recurring series list keeps the modal open and shows that appointment. */
  onNavigateToAppointment?: (appointment: Appointment) => void;
  showEditButton?: boolean; // Control whether to show the Edit button
  isCancelling?: boolean; // Loading state for cancellation
  isConfirming?: boolean; // Loading state for confirmation
  hideProfessionalAndBusiness?: boolean; // Hide Professional and Business sections (e.g., for My Appointments view)
  defaultSendUpdateEmail?: boolean; // Default checked state for "Send updates to customer"
  /** When false, hide professional name display in assignment blocks (1 user or business setting off). Default true. */
  showProfessionalNameInAssignment?: boolean;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  appointment,
  isOpen,
  onClose,
  onEdit,
  onCancel,
  onConfirm,
  onReschedule,
  onStatusChange,
  onNavigateToAppointment,
  showEditButton = true, // Default to true for backward compatibility
  isCancelling = false,
  isConfirming = false,
  hideProfessionalAndBusiness = false,
  defaultSendUpdateEmail = false,
  showProfessionalNameInAssignment = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [isMarkDepositPaidModalOpen, setIsMarkDepositPaidModalOpen] = useState(false);
  const [businessServices, setBusinessServices] = useState<any[]>([]);
  const [serviceCategories, setServiceCategories] = useState<Array<{ id: string; name: string; icon?: string; display_order?: number }>>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  /** Business professional row id for the logged-in user, when they appear in the roster (same id as user account). */
  const loggedInProfessionalId = useMemo(() => {
    if (!user?.id) return '';
    const uid = String(user.id);
    const match = (professionals || []).find((p: any) => String(p?.id) === uid);
    return match ? String(match.id) : '';
  }, [user?.id, professionals]);

  const [draftScheduledAtLocal, setDraftScheduledAtLocal] = useState<string>('');
  const [initialScheduledAtLocal, setInitialScheduledAtLocal] = useState<string>('');

  const [isServicePickerOpen, setIsServicePickerOpen] = useState(false);
  const [servicePickerMode, setServicePickerMode] = useState<'replace' | 'add'>('replace');
  const [servicePickerClientId, setServicePickerClientId] = useState<string>(''); // used for replace mode
  const [servicePickerSelected, setServicePickerSelected] = useState<Service | null>(null);
  const [servicePickerProfessionalId, setServicePickerProfessionalId] = useState<string>(''); // add mode only
  /** True after user changes the add-service professional dropdown (avoids overwriting explicit "No assignment"). */
  const servicePickerProfessionalTouchedRef = useRef(false);
  const [conflictingProfessionalIds, setConflictingProfessionalIds] = useState<Set<string>>(new Set());
  const [loadingProfessionalConflicts, setLoadingProfessionalConflicts] = useState(false);
  const conflictCheckReqIdRef = useRef(0);
  const prevScheduleIsoRef = useRef<string | null>(null);
  const activeAppointmentIdRef = useRef<string | null>(null);
  const scheduleTouchedRef = useRef(false);

  type DraftAssignment = {
    clientId: string; // stable UI key (supports new rows without DB ids)
    id?: string; // DB id (uuid) when present
    serviceId: string;
    serviceName: string;
    durationMinutes: number;
    price: number;
    serviceQuotable?: boolean;
    professionalId?: string;
    professionalFirstName?: string;
    professionalLastName?: string;
    professionalEmail?: string;
    notes?: string;
    insistOnProfessional?: boolean;
    fromDateTime?: string;
    toDateTime?: string;
  };
  const [draftAssignments, setDraftAssignments] = useState<DraftAssignment[]>([]);
  const [initialAssignmentsKey, setInitialAssignmentsKey] = useState<string>('');
  
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onConfirm: () => void;
    confirmButtonClass?: string;
    showCancellationNotes?: boolean;
    depositWarning?: string;
  } | null>(null);
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [noShow, setNoShow] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const [cancelScope, setCancelScope] = useState<'THIS' | 'REMAINING'>('THIS');
  const cancelScopeRef = useRef<'THIS' | 'REMAINING'>('THIS');
  const [seriesSummary, setSeriesSummary] = useState<AppointmentSeriesSummary | null>(null);
  const [isSeriesLoading, setIsSeriesLoading] = useState(false);
  const [isSeriesListModalOpen, setIsSeriesListModalOpen] = useState(false);
  const [isCustomerAppointmentsModalOpen, setIsCustomerAppointmentsModalOpen] = useState(false);
  const [seriesListItems, setSeriesListItems] = useState<AppointmentSeriesListItem[]>([]);
  const [seriesListLoading, setSeriesListLoading] = useState(false);
  const cancellationNotesRef = useRef('');
  const noShowRef = useRef(false);
  const sendCancellationEmailRef = useRef(false);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const [sendUpdateEmail, setSendUpdateEmail] = useState(defaultSendUpdateEmail);
  const [openTimePickerClientId, setOpenTimePickerClientId] = useState<string | null>(null);
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
  const [durationEditing, setDurationEditing] = useState<{
    assignmentId: string;
    clientId: string;
    serviceName: string;
    startIso: string;
    currentDurationMinutes: number;
  } | null>(null);
  const [durationHours, setDurationHours] = useState<string>('0');
  const [durationMinutes, setDurationMinutes] = useState<string>('00');
  const [isSavingDuration, setIsSavingDuration] = useState(false);
  /** Full GET /appointments/:id — enriches series quote totals; dashboard list rows often omit quoteFinalAgreedAmount on visits 2+. */
  const [fetchedAppointmentDetail, setFetchedAppointmentDetail] = useState<Appointment | null>(null);

  // Service location summary for this appointment (based on draft assignments + loaded services)
  const serviceLocationSummary = React.useMemo(() => {
    const serviceIdToLocation = new Map<string, string>();
    (businessServices || []).forEach((s: any) => {
      const id = String(s?.id ?? '');
      if (!id) return;
      const raw = s?.serviceLocation ?? s?.service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      if (loc) serviceIdToLocation.set(id, loc);
    });

    const ids: string[] =
      (draftAssignments || []).map((a) => String(a?.serviceId ?? '')).filter(Boolean);

    // Fallback to appointment assignments when draft isn't ready
    const fallbackIds: string[] =
      ids.length > 0 ? [] : ((appointment as any)?.assignments || []).map((a: any) => String(a?.serviceId ?? a?.service_id ?? '')).filter(Boolean);

    const allIds = ids.length > 0 ? ids : fallbackIds;
    const locations = allIds
      .map((id) => serviceIdToLocation.get(id))
      .filter((x): x is string => typeof x === 'string' && !!x);

    const hasOnSite = locations.some((l) => l === 'ON_SITE' || l === 'ONSITE');
    const hasInStore = locations.some((l) => l === 'IN_STORE' || l === 'INSTORE');

    const label =
      hasOnSite && hasInStore ? 'Mixed (In-store + On-site)'
      : hasOnSite ? 'On-site'
      : hasInStore ? 'In-store'
      : null;

    // Try to find a stored on-site address from appointment assignments (if returned by API)
    const rawAddr =
      ((appointment as any)?.assignments || []).find((a: any) => a?.serviceAddress || a?.service_address)?.serviceAddress ||
      ((appointment as any)?.assignments || []).find((a: any) => a?.serviceAddress || a?.service_address)?.service_address ||
      null;

    const addr = rawAddr && typeof rawAddr === 'object' ? rawAddr : null;
    const hasValidAddress = !!(
      addr &&
      typeof addr.address === 'string' && addr.address.trim() &&
      typeof addr.city === 'string' && addr.city.trim() &&
      typeof addr.state === 'string' && addr.state.trim() &&
      typeof addr.zip === 'string' && addr.zip.trim()
    );

    return {
      hasOnSite,
      label,
      address: hasValidAddress ? addr : null
    };
  }, [businessServices, draftAssignments, appointment]);

  const computeGapsMsByIndex = (rows: DraftAssignment[]): number[] => {
    // Gap after index i (can be negative for overlaps). Length will be rows.length - 1.
    const gaps: number[] = [];
    for (let i = 0; i < (rows || []).length - 1; i++) {
      const cur = rows[i];
      const next = rows[i + 1];
      const curTo = cur?.toDateTime ? new Date(cur.toDateTime) : null;
      const nextFrom = next?.fromDateTime ? new Date(next.fromDateTime) : null;
      if (!curTo || !nextFrom || Number.isNaN(curTo.getTime()) || Number.isNaN(nextFrom.getTime())) {
        gaps.push(0);
        continue;
      }
      gaps.push(nextFrom.getTime() - curTo.getTime());
    }
    return gaps;
  };

  // Close the time picker popover when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!openTimePickerClientId) return;
      const target = e.target as any;
      if (target?.closest?.('[data-timepicker-root="true"]')) return;
      setOpenTimePickerClientId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openTimePickerClientId]);

  const resequenceAssignmentsFromWithGaps = (rows: DraftAssignment[], anchor: Date, gapsMs: number[]): DraftAssignment[] => {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    if (!anchor || Number.isNaN(anchor.getTime())) return rows;

    let cursor = new Date(anchor);
    return rows.map((row, idx) => {
      const dur = Math.max(0, Number(row.durationMinutes) || 0);
      const from = new Date(cursor);
      const to = new Date(from.getTime() + dur * 60 * 1000);

      const gapAfter = Number(gapsMs?.[idx]) || 0; // last item uses 0
      cursor = new Date(to.getTime() + gapAfter);

      return { ...row, fromDateTime: from.toISOString(), toDateTime: to.toISOString() };
    });
  };

  const getResequenceAnchorDate = (rows: DraftAssignment[]): Date => {
    const explicitStarts = (rows || [])
      .map((r) => (r.fromDateTime ? new Date(r.fromDateTime) : null))
      .filter((d): d is Date => !!d && Number.isFinite(d.getTime()));

    if (explicitStarts.length > 0) {
      return new Date(Math.min(...explicitStarts.map((d) => d.getTime())));
    }

    const baseIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal) || appointment?.scheduledAt;
    const base = baseIso ? new Date(baseIso) : new Date();
    return Number.isNaN(base.getTime()) ? new Date() : base;
  };

  // Reset the checkbox each time the modal opens (or a different appointment is selected)
  useEffect(() => {
    if (!isOpen) return;
    setSendUpdateEmail(defaultSendUpdateEmail);
  }, [isOpen, appointment?.id, defaultSendUpdateEmail]);

  useEffect(() => {
    if (!isOpen) setIsCustomerAppointmentsModalOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !appointment?.id) {
      setFetchedAppointmentDetail(null);
      return;
    }
    setFetchedAppointmentDetail(null);
    let cancelled = false;
    apiService
      .getAppointment(appointment.id)
      .then((full) => {
        if (!cancelled) setFetchedAppointmentDetail(full);
      })
      .catch(() => {
        if (!cancelled) setFetchedAppointmentDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, appointment?.id]);

  const handleOpenSeriesListModal = React.useCallback(async () => {
    if (!appointment?.id) return;
    setIsSeriesListModalOpen(true);
    setSeriesListLoading(true);
    setSeriesListItems([]);
    try {
      const { appointments } = await apiService.getAppointmentSeriesAppointments(appointment.id);
      setSeriesListItems(appointments);
    } catch (err: any) {
      console.error('Error loading series appointments:', err);
      toast.error(err?.response?.data?.message || 'Could not load series appointments');
    } finally {
      setSeriesListLoading(false);
    }
  }, [appointment?.id]);

  // Reset transient/draft state on close so the next open starts clean.
  // This prevents leaking schedule state between different appointments (e.g., shifting assignment blocks by a prior appointment delta).
  useEffect(() => {
    if (isOpen && appointment) return;
    // Schedule draft + shift anchor + appointment identity
    setDraftScheduledAtLocal('');
    setInitialScheduledAtLocal('');
    prevScheduleIsoRef.current = null;
    activeAppointmentIdRef.current = null;
    scheduleTouchedRef.current = false;

    // Picker/UI transient state
    setIsServicePickerOpen(false);
    setServicePickerMode('replace');
    setServicePickerClientId('');
    setServicePickerSelected(null);
    setServicePickerProfessionalId('');
    servicePickerProfessionalTouchedRef.current = false;
    setConflictingProfessionalIds(new Set());
    setLoadingProfessionalConflicts(false);
    conflictCheckReqIdRef.current += 1;

    setOpenTimePickerClientId(null);
    setIsDurationModalOpen(false);
    setDurationEditing(null);

    setIsSeriesListModalOpen(false);
    setSeriesListItems([]);
    setSeriesListLoading(false);

    // Reset checkbox to default (open effect will set it too, but keep state sane while closed)
    setSendUpdateEmail(defaultSendUpdateEmail);
  }, [isOpen, appointment?.id, defaultSendUpdateEmail]);

  const openRemoveServiceConfirmation = (assignmentClientId: string, serviceLabel: string) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Remove Service',
      message: `Remove "${serviceLabel}" from this appointment? This will not be saved until you click Update.`,
      confirmText: 'Remove Service',
      cancelText: 'Go Back',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: () => {
        setConfirmationDialog(null);
        setDraftAssignments((prev) => prev.filter((x) => x.clientId !== assignmentClientId));
      }
    });
  };

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(target)) {
        setIsActionsDropdownOpen(false);
      }
    };

    if (isActionsDropdownOpen) {
      // Use a small timeout to ensure the event listener is added after the click that opened it
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 0);

    return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
    };
    }
  }, [isActionsDropdownOpen]);

  const handleStatusChange = async (
    newStatus: string,
    cancellationNotesText?: string,
    isNoShow?: boolean,
    sendEmail?: boolean,
    cancelScopeArg?: 'THIS' | 'REMAINING'
  ) => {
    if (!appointment) return;
    
    setIsUpdating(true);
    try {
      if (newStatus === 'CANCELLED') {
        // Always use the cancel endpoint when cancelling to properly handle noShow and cancellationNotes
        await apiService.cancelAppointment(appointment.id, {
          noShow: isNoShow || false,
          cancellationNotes: cancellationNotesText ? cancellationNotesText.trim() : '',
          ...(cancelScopeArg ? { cancelScope: cancelScopeArg } : {}),
          ...(sendEmail !== undefined ? { sendCancellationEmail: sendEmail } : {})
        });
        // Reload the appointment to get updated data
        const updatedAppointment = await apiService.getAppointment(appointment.id);
        if (onStatusChange) {
          onStatusChange(updatedAppointment);
        }
        if (onCancel) {
          onCancel(appointment.id, {
            noShow: isNoShow || false,
            cancellationNotes: cancellationNotesText ? cancellationNotesText.trim() : ''
          });
        }
        toast.success('Appointment cancelled successfully!');
      } else {
        // Use the regular update endpoint for other status changes
        const updatedAppointment = await apiService.updateAppointment(appointment.id, {
          status: newStatus as any
        });
        if (onStatusChange) {
          onStatusChange(updatedAppointment);
        }
        if (newStatus === 'CONFIRMED' && onConfirm) {
          onConfirm(appointment.id);
        }
        toast.success('Appointment status updated successfully!');
      }
    } catch (error: any) {
      console.error('Error updating appointment status:', error);
      toast.error(error.response?.data?.message || 'Failed to update appointment status');
      throw error; // Re-throw to prevent dialog from closing on error
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusChangeWithConfirmation = (newStatus: string, actionName: string) => {
    const messages: { [key: string]: { title: string; message: string; confirmText: string; confirmButtonClass: string; showCancellationNotes?: boolean } } = {
      'CONFIRMED': {
        title: 'Confirm Appointment',
        message: 'Are you sure you want to confirm this appointment?',
        confirmText: 'Confirm Appointment',
        confirmButtonClass: 'bg-green-600 hover:bg-green-700'
      },
      'COMPLETED': {
        title: 'Mark as Completed',
        message: 'Are you sure you want to mark this appointment as completed?',
        confirmText: 'Mark as Completed',
        confirmButtonClass: 'bg-blue-600 hover:bg-blue-700'
      },
      'CANCELLED': {
        title: 'Cancel Appointment',
        message: 'Are you sure you want to cancel this appointment?',
        confirmText: 'Cancel Appointment',
        confirmButtonClass: 'bg-red-600 hover:bg-red-700',
        showCancellationNotes: true
      }
    };

    const config = messages[newStatus];
    if (!config) return;

    const depositWarning =
      newStatus === 'CONFIRMED' && appointment && isPendingDepositOutstanding(appointment)
        ? 'This appointment has a Pending Deposit — the deposit has not been paid yet.'
        : undefined;

    // Reset cancellation notes, no show, and send email when opening dialog
    if (config.showCancellationNotes) {
      setCancellationNotes('');
      setNoShow(false);
      setSendCancellationEmail(false);
      setCancelScope('THIS');
      cancelScopeRef.current = 'THIS';
      setSeriesSummary(null);
      cancellationNotesRef.current = '';
      noShowRef.current = false;
      sendCancellationEmailRef.current = false;
    }

    setConfirmationDialog({
      isOpen: true,
      title: config.title,
      message: config.message,
      confirmText: config.confirmText,
      cancelText: 'Go Back',
      confirmButtonClass: config.confirmButtonClass,
      showCancellationNotes: config.showCancellationNotes,
      ...(depositWarning ? { depositWarning } : {}),
      onConfirm: async () => {
        // Use refs to get the latest values at execution time
        const currentNotes = cancellationNotesRef.current;
        const currentNoShow = noShowRef.current;
        const currentSendEmail = sendCancellationEmailRef.current;
        const currentCancelScope = cancelScopeRef.current;
        
        // Execute the action with captured values (don't close dialog yet)
        try {
          if (newStatus === 'CANCELLED') {
            await handleStatusChange(newStatus, currentNotes, currentNoShow, currentSendEmail, currentCancelScope);
          } else {
            await handleStatusChange(newStatus);
          }
          
          // Only close dialog and reset state after successful completion
          setConfirmationDialog(null);
          setCancellationNotes('');
          setNoShow(false);
          setSendCancellationEmail(false);
          setCancelScope('THIS');
          cancelScopeRef.current = 'THIS';
          setSeriesSummary(null);
          cancellationNotesRef.current = '';
          noShowRef.current = false;
          sendCancellationEmailRef.current = false;
        } catch (error) {
          // Error is already handled in handleStatusChange
          // Keep dialog open so user can try again or cancel
        }
      }
    });
  };

  // Load series summary when opening the cancel confirmation
  useEffect(() => {
    let cancelled = false;
    const isCancelDialogOpen = Boolean(confirmationDialog?.isOpen) && confirmationDialog?.title === 'Cancel Appointment';
    const appointmentId = appointment?.id;
    if (!isCancelDialogOpen || !appointmentId) return;

    setIsSeriesLoading(true);
    apiService
      .getAppointmentSeriesSummary(String(appointmentId))
      .then((data) => {
        if (cancelled) return;
        setSeriesSummary(data);
      })
      .catch(() => {
        if (cancelled) return;
        setSeriesSummary(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsSeriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [confirmationDialog?.isOpen, confirmationDialog?.title, appointment?.id]);
  const handleNoShowCancelClick = () => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Mark as No Show',
      message: 'The appointment is about to be canceled due to no show. Do you want to proceed?',
      confirmText: 'Yes, proceed',
      cancelText: 'Go Back',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          await handleStatusChange('CANCELLED', '', true);
          setConfirmationDialog(null);
        } catch (error) {
          // Error already handled in handleStatusChange; keep dialog open
        }
      }
    });
  };

  const handleMarkDepositPaidClick = () => {
    const detail = fetchedAppointmentDetail ?? appointment;
    if (!detail?.id || !isDepositOutstanding(detail)) return;
    setIsMarkDepositPaidModalOpen(true);
  };

  const handleMarkDepositPaidSubmit = async (payment: {
    paymentMethod: string;
    paymentDate: string;
    referenceNumber?: string | null;
    notes?: string | null;
  }) => {
    const detail = fetchedAppointmentDetail ?? appointment;
    if (!detail?.id) return;
    try {
      const updatedAppointment = await apiService.markAppointmentDepositPaid(detail.id, payment);
      setFetchedAppointmentDetail(updatedAppointment);
      onStatusChange?.(updatedAppointment);
      toast.success('Deposit marked as paid successfully!');
    } catch (error: any) {
      console.error('Error marking deposit paid:', error);
      toast.error(error.response?.data?.message || 'Failed to mark deposit as paid');
      throw error;
    }
  };

  const handleSendReminder = async () => {
    if (!appointment) return;
    
    setSendingReminder(true);
    try {
      const result = await apiService.sendAppointmentReminder(appointment.id);
      toast.success('Reminder email sent successfully!');
    } catch (error: any) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.message || 'Failed to send reminder email');
    } finally {
      setSendingReminder(false);
    }
  };

  // Load services and professionals when modal opens
  useEffect(() => {
    const loadServicesAndProfessionals = async () => {
      // Use currentBusiness.id if available, otherwise fall back to appointment.businessId
      const businessId = currentBusiness?.id || appointment?.businessId || (appointment as any)?.business_id;
      if (!isOpen || !businessId) return;
      
      setLoadingServices(true);
      try {
        const [servicesData, professionalsData, categoriesData] = await Promise.all([
          apiService.getBusinessServices(businessId),
          apiService.getBusinessProfessionalsDetailed(businessId),
          apiService.getServiceCategories(businessId).catch(() => [])
        ]);
        setBusinessServices(servicesData || []);
        setServiceCategories((categoriesData as any) || []);

        // Normalize professionals to match the shape used on the edit appointment page
        const formattedProfs = (professionalsData || []).map((prof: any) => ({
          id: prof.id,
          firstName: prof.name?.split(' ')[0] || prof.firstName || prof.first_name || '',
          lastName: prof.name?.split(' ').slice(1).join(' ') || prof.lastName || prof.last_name || '',
          email: prof.email,
          phone: prof.phone,
          canPerformServices: prof.canPerformServices
        }));
        setProfessionals(formattedProfs);
      } catch (error) {
        console.error('Error loading services and professionals:', error);
        toast.error('Failed to load services and professionals');
      } finally {
        setLoadingServices(false);
      }
    };

    loadServicesAndProfessionals();
  }, [isOpen, currentBusiness?.id, appointment?.businessId]);

  // If professionals load after opening "Add service", apply default selection without overriding an explicit empty choice.
  useEffect(() => {
    if (!isServicePickerOpen || servicePickerMode !== 'add') return;
    if (servicePickerProfessionalTouchedRef.current) return;
    if (servicePickerProfessionalId) return;
    if (!loggedInProfessionalId) return;
    setServicePickerProfessionalId(loggedInProfessionalId);
  }, [
    isServicePickerOpen,
    servicePickerMode,
    loggedInProfessionalId,
    servicePickerProfessionalId,
  ]);

  const toLocalDateTimeInputValue = (iso: string): string => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fromLocalDateTimeInputToIso = (value: string): string => {
    // `YYYY-MM-DDTHH:mm` is interpreted in the browser's local timezone.
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d.toISOString() : '';
  };

  const snapLocalDateTimeToHalfHour = (value: string): string => {
    // Keep empty/invalid values as-is (so we don't surprise the user while typing).
    if (!value) return value;
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return value;

    const minutes = d.getMinutes();
    // Snap to nearest 0/30.
    if (minutes < 15) {
      d.setMinutes(0, 0, 0);
    } else if (minutes < 45) {
      d.setMinutes(30, 0, 0);
    } else {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0, 0, 0);
    }
    return toLocalDateTimeInputValue(d.toISOString());
  };

  const splitLocalDateTimeInput = (value: string): { date: string; time: string } => {
    // value is expected to be "YYYY-MM-DDTHH:mm"
    if (!value || typeof value !== 'string') return { date: '', time: '' };
    const [date, time] = value.split('T');
    if (!date || !time) return { date: '', time: '' };
    return { date, time: time.slice(0, 5) };
  };

  const joinLocalDateTimeInput = (date: string, time: string): string => {
    if (!date || !time) return '';
    return `${date}T${time}`;
  };

  // Helper functions to convert between 24-hour format and 12-hour format with separate components
  const parseTimeTo12Hour = (time24: string): { hour: string; minute: string; ampm: string } => {
    if (!time24 || !time24.includes(':')) {
      return { hour: '9', minute: '00', ampm: 'AM' };
    }
    const [hours, minutes] = time24.split(':').map(Number);
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return {
      hour: String(hour12),
      minute: String(minutes).padStart(2, '0'),
      ampm: ampm
    };
  };

  const combine12HourTo24Hour = (hour: string, minute: string, ampm: string): string => {
    if (!hour || !minute || !ampm) return '09:00';
    let hour24 = Number(hour);
    if (ampm === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (ampm === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    return `${String(hour24).padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const HALF_HOUR_TIMES: string[] = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? '00' : '30';
    return `${String(h).padStart(2, '0')}:${m}`;
  });

  const MINUTE_OPTIONS_15: string[] = ['00', '15', '30', '45'];

  // Generate hour options (1-12)
  const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));
  
  // Generate minute options (00, 30)
  const MINUTE_OPTIONS = ['00', '30'];
  
  // AM/PM options
  const AMPM_OPTIONS = ['AM', 'PM'];

  // When adding a new service, optionally warn if a professional has an appointment conflict for that slot.
  useEffect(() => {
    if (!isServicePickerOpen || servicePickerMode !== 'add' || !servicePickerSelected || !appointment) {
      setConflictingProfessionalIds(new Set());
      setLoadingProfessionalConflicts(false);
      return;
    }
    const businessId = currentBusiness?.id || appointment.businessId;
    if (!businessId) return;

    const svc: any = servicePickerSelected;
    const durationMinutes = Number(svc?.durationMinutes ?? svc?.duration_minutes ?? 0) || 0;
    if (!durationMinutes || durationMinutes < 1) {
      setConflictingProfessionalIds(new Set());
      setLoadingProfessionalConflicts(false);
      return;
    }

    const baseIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal) || getAppointmentStartIso(appointment) || new Date().toISOString();
    const base = new Date(baseIso);

    // Prefer placing the new service after the latest existing block end (supports custom blocks + gaps).
    const existingEnds = (draftAssignments || [])
      .map((row) => (row.toDateTime ? new Date(row.toDateTime) : null))
      .filter((d): d is Date => !!d && Number.isFinite(d.getTime()));

    const fallbackOffsetMinutes = (draftAssignments || []).reduce((sum, row) => sum + (Number(row.durationMinutes) || 0), 0);
    const from = existingEnds.length
      ? new Date(Math.max(...existingEnds.map((d) => d.getTime())))
      : new Date(base.getTime() + fallbackOffsetMinutes * 60 * 1000);

    const to = new Date(from.getTime() + durationMinutes * 60 * 1000);

    const startQuery = new Date(from.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const endQuery = new Date(to.getTime() + 24 * 60 * 60 * 1000).toISOString();

    const reqId = ++conflictCheckReqIdRef.current;
    setLoadingProfessionalConflicts(true);

    (async () => {
      try {
        const checks = await Promise.all(
          (professionals || []).map(async (p: any) => {
            const proId = String(p.id);
            try {
              const resp = await apiService.getAppointments({
                businessId,
                professionalId: proId,
                startDate: startQuery,
                endDate: endQuery,
                status: 'upcoming',
              });
              const appts = resp?.data || [];
              const hasConflict = appts.some((apt: any) => {
                if (!apt?.id) return false;
                if (String(apt.id) === String(appointment.id)) return false;
                if (apt.status === 'CANCELLED' || apt.status === 'COMPLETED') return false;
                const slots: Array<{ slotFrom: Date; slotTo: Date }> = (apt.assignments || [])
                  .filter((as: any) => String(as.professionalId) === proId)
                  .map((as: any) => {
                    const slotFrom = new Date(as.fromDateTime || apt.scheduledAt);
                    const slotTo = new Date(as.toDateTime || apt.scheduledAt);
                    return { slotFrom, slotTo };
                  });
                return slots.some((slot) => slot.slotFrom < to && slot.slotTo > from);
              });
              return hasConflict ? proId : null;
            } catch {
              return null;
            }
          })
        );

        if (conflictCheckReqIdRef.current !== reqId) return;
        const next = new Set<string>();
        for (const id of checks) {
          if (id) next.add(id);
        }
        setConflictingProfessionalIds(next);
      } finally {
        if (conflictCheckReqIdRef.current === reqId) {
          setLoadingProfessionalConflicts(false);
        }
      }
    })();
  }, [
    isServicePickerOpen,
    servicePickerMode,
    servicePickerSelected,
    appointment?.id,
    appointment?.scheduledAt,
    currentBusiness?.id,
    draftScheduledAtLocal,
    draftAssignments,
    professionals,
  ]);

  // Initialize schedule edit state when appointment/timezone changes
  useEffect(() => {
    if (!isOpen || !appointment?.scheduledAt) return;
    const apptId = String(appointment.id);
    const nextInitial = toLocalDateTimeInputValue(appointment.scheduledAt);
    const isNewAppointment = activeAppointmentIdRef.current !== apptId;

    // When switching appointments, always overwrite draft schedule so we never carry over a previous appointment's date/time.
    if (isNewAppointment) {
      activeAppointmentIdRef.current = apptId;
      scheduleTouchedRef.current = false;
      setInitialScheduledAtLocal(nextInitial);
      setDraftScheduledAtLocal(nextInitial);
    } else {
      // Same appointment: keep user's in-progress edits, but refresh the "initial" baseline.
      setInitialScheduledAtLocal(nextInitial);
      setDraftScheduledAtLocal((prevDraft) => (scheduleTouchedRef.current ? prevDraft : nextInitial));
    }
    // Match what fromLocalDateTimeInputToIso(draftScheduledAtLocal) will produce after the state flush
    // (avoid spurious shift deltas vs raw appointment.scheduledAt).
    prevScheduleIsoRef.current =
      fromLocalDateTimeInputToIso(nextInitial) || appointment.scheduledAt || null;
  }, [isOpen, appointment?.id, appointment?.scheduledAt]);

  // If the appointment date/time is edited, shift all existing assignment blocks by the same delta.
  // This keeps custom blocks (overlaps/gaps) intact when moving an appointment.
  useEffect(() => {
    if (!isOpen || !appointment) return;
    const nextIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal);
    if (!nextIso) return;
    // While React is still flushing schedule state for a new appointment.id, draftScheduledAtLocal can
    // reflect the *previous* visit — shifting here corrupts drafts and marks assignments dirty ($0 quotable totals).
    if (appointment.scheduledAt) {
      const expectedLocal = toLocalDateTimeInputValue(appointment.scheduledAt);
      if (
        expectedLocal &&
        draftScheduledAtLocal &&
        draftScheduledAtLocal !== expectedLocal &&
        !scheduleTouchedRef.current
      ) {
        return;
      }
    }
    // Safety: never shift blocks across appointments (can happen if appointment identity changes without a close).
    if (activeAppointmentIdRef.current !== String(appointment.id)) {
      activeAppointmentIdRef.current = String(appointment.id);
      prevScheduleIsoRef.current = nextIso;
      scheduleTouchedRef.current = false;
      return;
    }
    const prevIso = prevScheduleIsoRef.current;
    if (!prevIso) {
      prevScheduleIsoRef.current = nextIso;
      return;
    }
    if (prevIso === nextIso) return;

    const deltaMs = new Date(nextIso).getTime() - new Date(prevIso).getTime();
    if (!Number.isFinite(deltaMs) || deltaMs === 0) {
      prevScheduleIsoRef.current = nextIso;
      return;
    }

    setDraftAssignments((prev) =>
      prev.map((row) => {
        const from = row.fromDateTime ? new Date(row.fromDateTime) : null;
        const to = row.toDateTime ? new Date(row.toDateTime) : null;
        if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return row;
        return {
          ...row,
          fromDateTime: new Date(from.getTime() + deltaMs).toISOString(),
          toDateTime: new Date(to.getTime() + deltaMs).toISOString(),
        };
      })
    );

    prevScheduleIsoRef.current = nextIso;
  }, [isOpen, appointment?.id, draftScheduledAtLocal]);

  // Initialize draft assignments when modal opens / appointment changes
  useEffect(() => {
    if (!isOpen || !appointment) return;
    const nextDraft: DraftAssignment[] = (appointment.assignments || []).map((a: any) => ({
      clientId: String(a.id),
      id: String(a.id),
      serviceId: String(a.serviceId),
      serviceName: String(a.serviceName || ''),
      durationMinutes: Number(a.durationMinutes || 0) || 0,
      price: typeof a.price === 'number' ? a.price : Number(a.price || 0) || 0,
      serviceQuotable: (a as any).serviceQuotable === true,
      professionalId: a.professionalId ? String(a.professionalId) : undefined,
      professionalFirstName: a.professionalFirstName,
      professionalLastName: a.professionalLastName,
      professionalEmail: a.professionalEmail,
      notes: a.notes,
      insistOnProfessional: a.insistOnProfessional === true,
      fromDateTime: a.fromDateTime || undefined,
      toDateTime: a.toDateTime || undefined,
    }));
    setDraftAssignments(nextDraft);
    const key = JSON.stringify(
      nextDraft.map((x) => ({
        id: x.id || null,
        serviceId: x.serviceId,
        professionalId: x.professionalId || null,
        fromDateTime: x.fromDateTime || null,
        toDateTime: x.toDateTime || null,
      }))
    );
    setInitialAssignmentsKey(key);
    setSendUpdateEmail(defaultSendUpdateEmail); // Reset checkbox when modal opens
  }, [isOpen, appointment?.id, defaultSendUpdateEmail]);

  const openDurationModalForAssignment = (row: DraftAssignment, idx: number) => {
    if (!appointment) return;
    if (!row?.id) {
      toast.error('This service is not saved yet. Please click Update first.');
      return;
    }
    if (appointment.status === 'CANCELLED' || appointment.status === 'COMPLETED') return;
    if (isApplyingChanges || isUpdating) return;

    // Determine a stable start time preview for "Ends at" calculation.
    const baseIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal) || getAppointmentStartIso(appointment) || new Date().toISOString();
    const base = new Date(baseIso);
    const offsetMinutes = (draftAssignments || []).slice(0, idx).reduce((sum, r) => sum + (Number(r.durationMinutes) || 0), 0);
    const sequentialStart = new Date(base.getTime() + offsetMinutes * 60 * 1000);
    const explicitStart = row.fromDateTime ? new Date(row.fromDateTime) : null;
    const start =
      explicitStart && Number.isFinite(explicitStart.getTime())
        ? explicitStart
        : sequentialStart;
    const startIso = start.toISOString();

    const cur = Math.max(0, Number(row.durationMinutes) || 0);
    const rounded = Math.max(15, Math.round(cur / 15) * 15);
    const h = Math.floor(rounded / 60);
    const m = rounded % 60;

    setDurationEditing({
      assignmentId: String(row.id),
      clientId: row.clientId,
      serviceName: row.serviceName || 'Service',
      startIso,
      currentDurationMinutes: cur
    });
    setDurationHours(String(h));
    setDurationMinutes(String(m).padStart(2, '0'));
    setIsDurationModalOpen(true);
  };

  const closeDurationModal = () => {
    if (isSavingDuration) return;
    setIsDurationModalOpen(false);
    setDurationEditing(null);
  };

  // (Service/professional editing inside the modal is draft-only; no dropdown click-outside handling needed.)

  if (!isOpen || !appointment) return null;

  const formatAppointmentDetails = (appointment: Appointment): string => {
    const iso = getAppointmentStartIso(appointment);
    if (!iso) return `Booking at ${appointment.business_name || 'Business'} (date TBD)`;
    const date = new Date(iso);
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `Booking at ${appointment.business_name || 'Business'} on ${dateStr}`;
  };



  const getStatusColor = (status: string, detail?: Appointment | null) => {
    if (detail && isPendingDepositOutstanding(detail)) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    switch (status) {
      case 'CONFIRMED': return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CANCELLED': return 'bg-red-100 text-red-800 border-red-200';
      case 'COMPLETED': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const formatStatus = (status: string, detail?: Appointment | null) => formatAppointmentStatusLabel(status, detail ?? undefined);

  const formatDateOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };
  const formatTimeOnly = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  const getAppointmentDateTimeDisplayString = (apt: Appointment): string => {
    // Prefer firm scheduledAt (e.g. after reschedule) over a leftover flex window
    if (apt.scheduledAt) {
      return `${formatDateOnly(apt.scheduledAt)} ${formatTimeOnly(apt.scheduledAt)}`;
    }
    const flex = (apt as any).flexScheduledAt;
    if (flex?.earliest && flex?.latest) {
      return `${formatDateOnly(flex.earliest)} Between ${formatTimeOnly(flex.earliest)} – ${formatTimeOnly(flex.latest)}`;
    }
    const iso = getAppointmentStartIso(apt);
    if (!iso) return 'Date TBD';
    return `${formatDateOnly(iso)} ${formatTimeOnly(iso)}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to get service names from assignments
  const getServiceNames = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.map((assignment) => assignment.serviceName).join(', ');
    }
    return 'No services assigned';
  };

  // Helper function to get total duration from assignments
  const getTotalDuration = (appointment: Appointment) => {
    return getTotalDurationHelper(appointment);
  };

  // Helper function to get total price from assignments
  const getTotalPrice = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      return appointment.assignments.reduce((total, assignment) => {
        // Handle both string and number price values
        let price = 0;
        if (typeof assignment.price === 'number') {
          price = assignment.price;
        } else if (typeof assignment.price === 'string') {
          price = parseFloat(assignment.price) || 0;
        }
        return total + price;
      }, 0);
    }
    return 0;
  };

  // Helper function to get professional names from assignments
  const getProfessionalNames = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      const professionals = appointment.assignments
        .map((assignment) => assignment.professionalFirstName && assignment.professionalLastName 
          ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.` 
          : null)
        .filter(Boolean);
      return Array.from(new Set(professionals)).join(', ');
    }
    return '';
  };

  // Helper function to get first professional initials
  const getFirstProfessionalInitials = (appointment: Appointment) => {
    if (appointment.assignments && appointment.assignments.length > 0) {
      const firstAssignment = appointment.assignments[0];
      if (firstAssignment.professionalFirstName && firstAssignment.professionalLastName) {
        return `${firstAssignment.professionalFirstName[0]}${firstAssignment.professionalLastName[0]}`;
      }
    }
    return '';
  };

  // Helper function to get service icon
  const getServiceIcon = (service: any): string | null => {
    const serviceIcon = service?.icon || (service as any)?.icon;
    if (serviceIcon && typeof serviceIcon === 'string' && serviceIcon.trim() !== '') {
      return serviceIcon;
    }
    const categoryIcon = (service?.category as any)?.icon || service?.categoryIcon;
    if (categoryIcon && typeof categoryIcon === 'string' && categoryIcon.trim() !== '') {
      return categoryIcon;
    }
    return null;
  };

  // Helper function to get professional display name
  const getProfessionalDisplayName = (prof: any): string => {
    if (!prof) return '';
    return `${prof.firstName || ''} ${prof.lastName || ''}`.trim() || prof.email || 'Unknown';
  };

  // Only treat schedule as dirty if the user actually interacted with the schedule controls
  // during this modal session. This prevents accidentally sending scheduledAt due to stale state
  // or minor normalization differences when switching appointments.
  const scheduleDirty =
    scheduleTouchedRef.current &&
    !!draftScheduledAtLocal &&
    !!initialScheduledAtLocal &&
    draftScheduledAtLocal !== initialScheduledAtLocal;

  const assignmentsDirty = (() => {
    // Must match the shape used when setting initialAssignmentsKey (open / after save);
    // previously omitted fromDateTime/toDateTime so key !== initial always and totals stayed on line-sum ($0 quotable).
    const key = JSON.stringify(
      (draftAssignments || []).map((x) => ({
        id: x.id || null,
        serviceId: x.serviceId,
        professionalId: x.professionalId || null,
        fromDateTime: x.fromDateTime || null,
        toDateTime: x.toDateTime || null,
      }))
    );
    return key !== initialAssignmentsKey;
  })();

  const hasPendingChanges = scheduleDirty || assignmentsDirty;

  const quoteFinalForTotals =
    fetchedAppointmentDetail?.quoteFinalAgreedAmount ?? appointment.quoteFinalAgreedAmount;

  const displayTotalPrice = assignmentsDirty
    ? (draftAssignments || []).reduce((sum, row) => sum + (Number(row.price) || 0), 0)
    : quoteFinalForTotals != null && !Number.isNaN(Number(quoteFinalForTotals))
      ? Number(quoteFinalForTotals)
      : (draftAssignments || []).reduce((sum, row) => sum + (Number(row.price) || 0), 0);

  const seriesAppointmentForSchedule = fetchedAppointmentDetail ?? appointment;
  const hasRecurringSeriesForSchedule = Boolean(
    seriesAppointmentForSchedule &&
      (seriesAppointmentForSchedule.seriesId || (seriesAppointmentForSchedule as any).series_id)
  );
  const historyCustomerId = String(appointment.customerId || (appointment as any).customer_id || '');
  const historyCustomerName = `${appointment.customer_first_name || ''} ${appointment.customer_last_name || ''}`.trim();
  const canOpenCustomerHistory = Boolean(historyCustomerId && currentBusiness?.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-1">
              <h2 className="text-xl font-bold text-gray-900 leading-tight">Appointment Details</h2>
              <span className={`hidden md:inline-flex px-3 py-1 rounded-full text-sm font-medium border flex-shrink-0 ${getStatusColor(appointment.status, appointment)}`}>
                {formatStatus(appointment.status, appointment)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {getAppointmentDateTimeDisplayString(appointment)}
            </p>
            <span className={`md:hidden inline-flex px-3 py-1 rounded-full text-sm font-medium border mt-2 ${getStatusColor(appointment.status, appointment)}`}>
              {formatStatus(appointment.status, appointment)}
            </span>
          </div>
          <div className="flex items-start gap-2">
            {/* Actions Dropdown */}
            {appointment?.status !== 'CANCELLED' && appointment?.status !== 'COMPLETED' && (
              <div className="relative" ref={actionsDropdownRef}>
                <button
                  onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  title="Actions"
                >
                  <Settings className="w-6 h-6 md:w-4 md:h-4" />
                </button>
                {isActionsDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 min-w-[240px] bg-white border border-gray-200 rounded-lg shadow-xl z-[99999] py-2">
                    {appointment?.status === 'PENDING' && (
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleStatusChangeWithConfirmation('CONFIRMED', 'Confirm Appointment');
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-check-circle text-green-600"></i>
                        Confirm Appointment
                      </button>
                    )}
                    {isDepositOutstanding(fetchedAppointmentDetail ?? appointment) && (
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleMarkDepositPaidClick();
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-cash-coin text-green-600"></i>
                        Mark Deposit Paid
                      </button>
                    )}
                    {appointment?.status === 'CONFIRMED' && (
                      <button
                        onClick={() => {
                          setIsActionsDropdownOpen(false);
                          handleStatusChangeWithConfirmation('COMPLETED', 'Mark as Completed');
                        }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                      >
                        <i className="bi bi-check2-square text-green-600"></i>
                        Mark as Completed
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setIsActionsDropdownOpen(false);
                        handleNoShowCancelClick();
                      }}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      Mark as No Show
                    </button>
                    <button
                      onClick={() => {
                        setIsActionsDropdownOpen(false);
                        handleStatusChangeWithConfirmation('CANCELLED', 'Cancel Appointment');
                      }}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold"
                    >
                      <i className="bi bi-x-octagon text-red-600"></i>
                      Cancel Appointment
                    </button>
                    <div className="h-px bg-gray-200 my-2"></div>
                    <button
                      onClick={() => {
                        setIsActionsDropdownOpen(false);
                        handleSendReminder();
                      }}
                      disabled={sendingReminder || appointment?.status === 'PENDING'}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="bi bi-bell text-amber-600"></i>
                      Send Reminder
                    </button>
                    <div className="h-px bg-gray-200 my-2"></div>
                    <button
                      onClick={() => {
                        setIsActionsDropdownOpen(false);
                        if (!appointment?.id || !currentBusiness?.id) return;
                        onClose();
                        if (appointment.invoiceId) {
                          navigate(`/dashboard/business/invoices/${appointment.invoiceId}`, {
                            state: { from: `${location.pathname}${location.search || ''}` },
                          });
                        } else {
                          navigate(`/dashboard/business/appointments/${appointment.id}/create-invoice`, {
                            state: { from: `${location.pathname}${location.search || ''}` },
                          });
                        }
                      }}
                      disabled={!appointment?.id || !currentBusiness?.id}
                      className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="bi bi-receipt text-blue-600"></i>
                      {appointment.invoiceId ? 'View Invoice' : 'Create Invoice'}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-7 w-7 md:h-5 md:w-5 text-gray-500" />
            </button>
          </div>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Customer Information */}
          <div className="md:bg-gray-50 md:rounded-lg md:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <User className="h-5 w-5 text-green-600 mr-2" />
              Customer Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">
                    {appointment.customer_first_name?.[0]}{appointment.customer_last_name?.[0]}
                  </span>
                </div>
                <div>
                  {canOpenCustomerHistory ? (
                    <button
                      type="button"
                      onClick={() => setIsCustomerAppointmentsModalOpen(true)}
                      className="font-medium text-[#0B4778] hover:underline text-left"
                    >
                      {historyCustomerName || 'Customer'}
                    </button>
                  ) : (
                    <p className="font-medium text-gray-900">{historyCustomerName || 'Customer'}</p>
                  )}
                  {appointment.customer_email && !appointment.customer_email.toLowerCase().startsWith('temp-') && (
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                      <i className="bi bi-envelope"></i>
                      <span>{appointment.customer_email}</span>
                    </p>
                  )}
                  {appointment.customer_phone && (
                    <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                      <i className="bi bi-telephone"></i>
                      <span>{displayCustomerPhone(appointment.customer_phone, canViewFullCustomerPhone(user, currentBusiness?.id || appointment.businessId))}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Service Location (only when at least one selected service is ON_SITE) */}
          {serviceLocationSummary.hasOnSite && (
            <div className="md:bg-gray-50 md:rounded-lg md:p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <MapPin className="h-5 w-5 text-emerald-600 mr-2" />
                Service Location
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</span>
                  <div className="mt-1 font-medium text-gray-900">{serviceLocationSummary.label || 'On-site'}</div>
                </div>
                {serviceLocationSummary.address && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="font-medium text-gray-900">{serviceLocationSummary.address.address}</div>
                    <div>
                      {serviceLocationSummary.address.city}, {serviceLocationSummary.address.state} {serviceLocationSummary.address.zip}
                    </div>
                    {serviceLocationSummary.address.locationNotes && String(serviceLocationSummary.address.locationNotes).trim() !== '' && (
                      <div className="mt-2 text-gray-600">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                        <div className="mt-1">{serviceLocationSummary.address.locationNotes}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Schedule Information */}
          <div className="md:bg-gray-50 md:rounded-lg md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 text-orange-600 mr-2" />
                Schedule
              </h3>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Date/Time</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <input
                    type="date"
                    value={splitLocalDateTimeInput(draftScheduledAtLocal).date}
                    onChange={(e) => {
                      const { time } = splitLocalDateTimeInput(draftScheduledAtLocal);
                      scheduleTouchedRef.current = true;
                      setDraftScheduledAtLocal(joinLocalDateTimeInput(e.target.value, time || '09:00'));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    disabled={isApplyingChanges || isUpdating || appointment?.status === 'CANCELLED' || appointment?.status === 'COMPLETED'}
                  />
                  <p className="text-xs text-gray-500 mt-1 hidden sm:block">Date</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <select
                      value={parseTimeTo12Hour(splitLocalDateTimeInput(draftScheduledAtLocal).time).hour}
                      onChange={(e) => {
                        const { date, time } = splitLocalDateTimeInput(draftScheduledAtLocal);
                        const current = parseTimeTo12Hour(time || '09:00');
                        const newTime = combine12HourTo24Hour(e.target.value, current.minute, current.ampm);
                        scheduleTouchedRef.current = true;
                        setDraftScheduledAtLocal(joinLocalDateTimeInput(date, newTime));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      disabled={isApplyingChanges || isUpdating || appointment?.status === 'CANCELLED' || appointment?.status === 'COMPLETED'}
                    >
                      {HOUR_OPTIONS.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Hour</p>
                  </div>
                  <div>
                    <select
                      value={parseTimeTo12Hour(splitLocalDateTimeInput(draftScheduledAtLocal).time).minute}
                      onChange={(e) => {
                        const { date, time } = splitLocalDateTimeInput(draftScheduledAtLocal);
                        const current = parseTimeTo12Hour(time || '09:00');
                        const newTime = combine12HourTo24Hour(current.hour, e.target.value, current.ampm);
                        scheduleTouchedRef.current = true;
                        setDraftScheduledAtLocal(joinLocalDateTimeInput(date, newTime));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      disabled={isApplyingChanges || isUpdating || appointment?.status === 'CANCELLED' || appointment?.status === 'COMPLETED'}
                    >
                      {MINUTE_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Minute</p>
                  </div>
                  <div>
                    <select
                      value={parseTimeTo12Hour(splitLocalDateTimeInput(draftScheduledAtLocal).time).ampm}
                      onChange={(e) => {
                        const { date, time } = splitLocalDateTimeInput(draftScheduledAtLocal);
                        const current = parseTimeTo12Hour(time || '09:00');
                        const newTime = combine12HourTo24Hour(current.hour, current.minute, e.target.value);
                        scheduleTouchedRef.current = true;
                        setDraftScheduledAtLocal(joinLocalDateTimeInput(date, newTime));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      disabled={isApplyingChanges || isUpdating || appointment?.status === 'CANCELLED' || appointment?.status === 'COMPLETED'}
                    >
                      {AMPM_OPTIONS.map((ap) => (
                        <option key={ap} value={ap}>
                          {ap}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Period</p>
                  </div>
                </div>
              </div>
              {hasRecurringSeriesForSchedule && seriesAppointmentForSchedule && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => void handleOpenSeriesListModal()}
                    aria-label="View all appointments in this series"
                    className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-[#0B4778]/25 bg-[#0B4778]/5 px-3 py-2 text-left text-sm text-[#0B4778] hover:bg-[#0B4778]/10 transition-colors w-full sm:w-auto"
                  >
                    <Repeat2 className="w-4 h-4 flex-shrink-0" aria-hidden />
                    <span className="font-semibold">Recurring series</span>
                    {seriesAppointmentForSchedule.occurrenceIndex != null &&
                    seriesAppointmentForSchedule.seriesOccurrenceCount != null &&
                    Number(seriesAppointmentForSchedule.seriesOccurrenceCount) > 0 ? (
                      <span className="text-gray-700 font-normal">
                        · Visit {seriesAppointmentForSchedule.occurrenceIndex} of{' '}
                        {seriesAppointmentForSchedule.seriesOccurrenceCount}
                      </span>
                    ) : null}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Service Information */}
          <div className="md:bg-gray-50 md:rounded-lg md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 text-blue-600 mr-2" />
              Services & Charges
            </h3>
              <button
                onClick={() => {
                  // Add a new service via the same service-selection modal (draft only)
                  setServicePickerMode('add');
                  setServicePickerClientId('');
                  setServicePickerSelected(null);
                  servicePickerProfessionalTouchedRef.current = false;
                  setServicePickerProfessionalId(loggedInProfessionalId);
                  setIsServicePickerOpen(true);
                }}
                className="aacore-btn2-primary"
                title="Add Service"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Service Items */}
            <div className="space-y-3 mb-4">
              {draftAssignments.map((a, idx) => {
                const svc = businessServices.find((s) => String(s.id) === String(a.serviceId));
                const icon = svc ? getServiceIcon(svc) : null;
                const categoryName = (svc?.category as any)?.name || svc?.categoryName || '';

                // Display (and edit) start times per assignment.
                // Prefer explicit times when present; otherwise fall back to sequential display based on draft schedule time.
                const baseIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal) || getAppointmentStartIso(appointment) || new Date().toISOString();
                const base = new Date(baseIso);
                const offsetMinutes = draftAssignments.slice(0, idx).reduce((sum, row) => sum + (Number(row.durationMinutes) || 0), 0);
                const sequentialStart = new Date(base.getTime() + offsetMinutes * 60 * 1000);

                const explicitStart = a.fromDateTime ? new Date(a.fromDateTime) : null;
                const start =
                  explicitStart && Number.isFinite(explicitStart.getTime())
                    ? explicitStart
                    : sequentialStart;

                const startLabel = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

                // Keep end time derived for persistence (toDateTime), but don't display it in the UI.
                const end = new Date(start.getTime() + (Number(a.durationMinutes) || 0) * 60 * 1000);

                const time24 = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
                const current12 = parseTimeTo12Hour(time24);
                const minuteValue = String(start.getMinutes()).padStart(2, '0');
                const minuteOptions =
                  minuteValue && !MINUTE_OPTIONS_15.includes(minuteValue)
                    ? Array.from(new Set([minuteValue, ...MINUTE_OPTIONS_15]))
                    : MINUTE_OPTIONS_15;

                const canEditDuration =
                  appointment.status !== 'CANCELLED' &&
                  appointment.status !== 'COMPLETED' &&
                  Boolean(a.id) &&
                  !isApplyingChanges &&
                  !isUpdating;

                return (
                  <div key={a.clientId} className="bg-white border border-gray-200 rounded-lg shadow-sm p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 font-semibold text-sm flex-shrink-0">
                          <Clock className="w-4 h-4" />
                          <div className="relative" data-timepicker-root="true">
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  isApplyingChanges ||
                                  isUpdating ||
                                  appointment?.status === 'CANCELLED' ||
                                  appointment?.status === 'COMPLETED'
                                ) {
                                  return;
                                }
                                setOpenTimePickerClientId((prev) => (prev === a.clientId ? null : a.clientId));
                              }}
                              className="inline-flex items-center gap-2 bg-transparent border-0 outline-none focus:outline-none"
                              title="Change start time"
                            >
                              <span className="text-blue-700">{startLabel}</span>
                              <ChevronDown className="w-4 h-4 text-blue-600" />
                            </button>

                            {openTimePickerClientId === a.clientId && (
                              <div className="absolute left-0 top-full mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-[260px]">
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="min-w-0">
                                    <div className="text-[11px] font-semibold text-gray-500 mb-2">Hours</div>
                                    <div className="max-h-44 overflow-auto rounded-lg border border-gray-100">
                                      {HOUR_OPTIONS.map((h) => {
                                        const isActive = h === current12.hour;
                                        return (
                                          <button
                                            key={h}
                                            type="button"
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                                              isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'
                                            }`}
                                            onClick={() => {
                                              const datePart =
                                                splitLocalDateTimeInput(draftScheduledAtLocal).date ||
                                                splitLocalDateTimeInput(toLocalDateTimeInputValue(getAppointmentStartIso(appointment) ?? new Date().toISOString())).date;
                                              if (!datePart) return;
                                              const time = combine12HourTo24Hour(h, current12.minute, current12.ampm);
                                              const local = joinLocalDateTimeInput(datePart, time);
                                              const iso = fromLocalDateTimeInputToIso(local);
                                              if (!iso) return;
                                              const from = new Date(iso);
                                              if (Number.isNaN(from.getTime())) return;
                                              const to = new Date(from.getTime() + (Number(a.durationMinutes) || 0) * 60 * 1000);

                                              setDraftAssignments((prev) =>
                                                prev.map((row) =>
                                                  row.clientId === a.clientId
                                                    ? { ...row, fromDateTime: from.toISOString(), toDateTime: to.toISOString() }
                                                    : row
                                                )
                                              );
                                            }}
                                          >
                                            {h}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  <div className="min-w-0">
                                    <div className="text-[11px] font-semibold text-gray-500 mb-2">Minutes</div>
                                    <div className="rounded-lg border border-gray-100 overflow-hidden">
                                      {minuteOptions.map((m) => {
                                        const isActive = m === minuteValue;
                                        return (
                                          <button
                                            key={m}
                                            type="button"
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                                              isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'
                                            }`}
                                            onClick={() => {
                                              const datePart =
                                                splitLocalDateTimeInput(draftScheduledAtLocal).date ||
                                                splitLocalDateTimeInput(toLocalDateTimeInputValue(getAppointmentStartIso(appointment) ?? new Date().toISOString())).date;
                                              if (!datePart) return;
                                              const time = combine12HourTo24Hour(current12.hour, m, current12.ampm);
                                              const local = joinLocalDateTimeInput(datePart, time);
                                              const iso = fromLocalDateTimeInputToIso(local);
                                              if (!iso) return;
                                              const from = new Date(iso);
                                              if (Number.isNaN(from.getTime())) return;
                                              const to = new Date(from.getTime() + (Number(a.durationMinutes) || 0) * 60 * 1000);

                                              setDraftAssignments((prev) =>
                                                prev.map((row) =>
                                                  row.clientId === a.clientId
                                                    ? { ...row, fromDateTime: from.toISOString(), toDateTime: to.toISOString() }
                                                    : row
                                                )
                                              );

                                              setOpenTimePickerClientId(null);
                                            }}
                                          >
                                            {m}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  <div className="min-w-0">
                                    <div className="text-[11px] font-semibold text-gray-500 mb-2">AM/PM</div>
                                    <div className="rounded-lg border border-gray-100 overflow-hidden">
                                      {AMPM_OPTIONS.map((ap) => {
                                        const isActive = ap === current12.ampm;
                                        return (
                                          <button
                                            key={ap}
                                            type="button"
                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                                              isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-800'
                                            }`}
                                            onClick={() => {
                                              const datePart =
                                                splitLocalDateTimeInput(draftScheduledAtLocal).date ||
                                                splitLocalDateTimeInput(toLocalDateTimeInputValue(getAppointmentStartIso(appointment) ?? new Date().toISOString())).date;
                                              if (!datePart) return;
                                              const time = combine12HourTo24Hour(current12.hour, current12.minute, ap);
                                              const local = joinLocalDateTimeInput(datePart, time);
                                              const iso = fromLocalDateTimeInputToIso(local);
                                              if (!iso) return;
                                              const from = new Date(iso);
                                              if (Number.isNaN(from.getTime())) return;
                                              const to = new Date(from.getTime() + (Number(a.durationMinutes) || 0) * 60 * 1000);

                                              setDraftAssignments((prev) =>
                                                prev.map((row) =>
                                                  row.clientId === a.clientId
                                                    ? { ...row, fromDateTime: from.toISOString(), toDateTime: to.toISOString() }
                                                    : row
                                                )
                                              );

                                              setOpenTimePickerClientId(null);
                                            }}
                                          >
                                            {ap}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (idx === 0) return;
                            setDraftAssignments((prev) => {
                              const gaps = computeGapsMsByIndex(prev);
                              const next = [...prev];
                              const tmp = next[idx - 1];
                              next[idx - 1] = next[idx];
                              next[idx] = tmp;
                              const anchor = getResequenceAnchorDate(next);
                              return resequenceAssignmentsFromWithGaps(next, anchor, gaps);
                            });
                          }}
                          disabled={idx === 0 || isApplyingChanges || isUpdating}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ArrowUp className="w-4 h-4 text-gray-700" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (idx === draftAssignments.length - 1) return;
                            setDraftAssignments((prev) => {
                              const gaps = computeGapsMsByIndex(prev);
                              const next = [...prev];
                              const tmp = next[idx + 1];
                              next[idx + 1] = next[idx];
                              next[idx] = tmp;
                              const anchor = getResequenceAnchorDate(next);
                              return resequenceAssignmentsFromWithGaps(next, anchor, gaps);
                            });
                          }}
                          disabled={idx === draftAssignments.length - 1 || isApplyingChanges || isUpdating}
                          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ArrowDown className="w-4 h-4 text-gray-700" />
                        </button>
                        {/* Intentionally omit the descriptive "Starts at..." text per UX request */}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            const label = (svc?.name || a.serviceName || 'Service').trim();
                            openRemoveServiceConfirmation(a.clientId, label);
                          }}
                          disabled={isApplyingChanges || isUpdating}
                          className="aacore-btn2-danger disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Remove service"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                      <button
                        type="button"
                        onClick={() => {
                          if (isApplyingChanges || isUpdating) return;
                          setServicePickerMode('replace');
                          setServicePickerClientId(a.clientId);
                          // Pre-select current service (single-select)
                          const current = (businessServices || []).find((s: any) => String(s.id) === String(a.serviceId));
                          setServicePickerSelected((current as any) || null);
                          setServicePickerProfessionalId(a.professionalId ? String(a.professionalId) : '');
                          setIsServicePickerOpen(true);
                        }}
                        disabled={loadingServices || isApplyingChanges || isUpdating}
                        className="w-full min-w-0 text-left border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors p-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        title="Change service (coming soon)"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                              {icon ? (
                                <span className="text-xl">{icon}</span>
                              ) : (
                                <i className="bi bi-star-fill" style={{ fontSize: '20px', color: '#FBBF24' }}></i>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 line-clamp-2">
                                {svc?.name || a.serviceName || 'Service'}
                              </div>
                              {(() => {
                                const rawLoc = (svc as any)?.serviceLocation ?? (svc as any)?.service_location;
                                const loc = typeof rawLoc === 'string' ? rawLoc.toUpperCase().trim() : '';
                                const isOnSite = loc === 'ON_SITE' || loc === 'ONSITE';
                                if (!isOnSite) return null;
                                return (
                                  <div className="mt-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                      On-Site
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </button>

                      <div className="min-w-0 flex">
                        <select
                          value={a.professionalId || ''}
                          onChange={(e) => {
                            const nextPro = e.target.value || undefined;
                            setDraftAssignments((prev) =>
                              prev.map((row) => (row.clientId === a.clientId ? { ...row, professionalId: nextPro } : row))
                            );
                          }}
                          disabled={isApplyingChanges || isUpdating}
                          className="w-full h-full min-h-[52px] px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50"
                          title="Professional"
                        >
                          <option value="">No assignment</option>
                          {professionals.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {getProfessionalDisplayName(p)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
                      <button
                        type="button"
                        onClick={() => openDurationModalForAssignment(a, idx)}
                        disabled={!canEditDuration}
                        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${
                          canEditDuration
                            ? 'border-[#0B4778]/20 bg-[#0B4778]/5 text-[#0B4778] hover:bg-[#0B4778]/10'
                            : 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                        }`}
                        title={canEditDuration ? 'Edit duration' : 'Duration cannot be edited'}
                      >
                        <CalendarClock className="w-3.5 h-3.5" />
                        <span>{a.durationMinutes} min</span>
                      </button>
                      <span className="text-gray-300">•</span>
                      {a.serviceQuotable === true && (Number(a.price) === 0 || a.price == null) ? (
                        <span
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700"
                          title="Quotable price"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </span>
                      ) : (
                        <span className="font-mono">${Number(a.price || 0).toFixed(2)}</span>
                      )}
                      {categoryName ? (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-600">{categoryName}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totals */}
            <div className="flex flex-col items-end gap-1 max-w-[420px] ml-auto">
              <div className="flex justify-between items-center w-full text-sm">
                <span className="text-gray-600 text-xs">Subtotal</span>
                <strong className="font-mono">${displayTotalPrice.toFixed(2)}</strong>
              </div>
              <div className="flex justify-between items-center w-full text-lg pt-2 border-t border-gray-200">
                <strong>Total</strong>
                <strong className="font-mono">${displayTotalPrice.toFixed(2)}</strong>
              </div>
              {appointment?.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                <>
                  <div className="flex justify-between items-center w-full text-sm pt-2">
                    <span className="text-gray-600 text-xs">Deposit paid</span>
                    <strong className="font-mono">${Number(appointment.receivedDepositAmount).toFixed(2)}</strong>
                  </div>
                  {appointment.depositPaidAt && (
                    <div className="flex justify-end w-full text-xs text-gray-500">
                      {formatDate(appointment.depositPaidAt)} at {formatTime(appointment.depositPaidAt)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="md:bg-gray-50 md:rounded-lg md:p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Notes</h3>
              <p className="text-gray-700 text-sm">{appointment.notes}</p>
            </div>
          )}

          {/* Cancellation Information */}
          {appointment.status === 'CANCELLED' && (
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h3 className="font-semibold text-red-900 mb-3 flex items-center">
                <X className="h-5 w-5 text-red-600 mr-2" />
                Cancellation Details
              </h3>
              <div className="space-y-2">
                {appointment.noShow && (
                  <div className="flex items-center space-x-2">
                    <span className="text-red-700 font-medium">No Show:</span>
                    <span className="text-red-600 bg-red-100 px-2 py-1 rounded text-sm">Customer did not show up</span>
                  </div>
                )}
                {appointment.cancellationNotes && (
                  <div>
                    <span className="text-red-700 font-medium">Cancellation Notes:</span>
                    <p className="text-red-600 text-sm mt-1">{appointment.cancellationNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Created/Updated Info */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>Created: {new Date(appointment.createdAt).toLocaleString()}</p>
            <p>Updated: {new Date(appointment.updatedAt).toLocaleString()}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 p-6 border-t border-gray-200">
          {hasPendingChanges && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="send-update-email"
                checked={sendUpdateEmail}
                onChange={(e) => setSendUpdateEmail(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-400 hover:border-gray-500 rounded focus:ring-blue-500"
                disabled={isApplyingChanges || isUpdating}
              />
              <label
                htmlFor="send-update-email"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Send updates to customer
              </label>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="aacore-btn2-secondary flex-1 sm:flex-none"
            >
              <X className="w-4 h-4" />
              <span>Close</span>
            </button>
            <button
            onClick={async () => {
              if (!appointment) return;
              if (!hasPendingChanges) return;
              if ((draftAssignments || []).length === 0) return;

              const payload: any = { allowOverride: true };

              if (scheduleDirty) {
                if (!draftScheduledAtLocal) {
                  toast.error('Please select a date and time');
                  return;
                }
                const iso = fromLocalDateTimeInputToIso(draftScheduledAtLocal);
                if (!iso) {
                  toast.error('Invalid date/time');
                  return;
                }
                payload.scheduledAt = iso;
              }

              if (assignmentsDirty) {
                payload.assignments = draftAssignments.map((row) => ({
                  ...(row.id ? { id: row.id } : {}),
                  serviceId: row.serviceId,
                  professionalId: row.professionalId || undefined,
                  durationMinutes: row.durationMinutes,
                  price: row.serviceQuotable && (row.price === 0 || row.price == null) ? null : row.price,
                  notes: row.notes,
                  insistOnProfessional: row.insistOnProfessional === true,
                  fromDateTime: row.fromDateTime,
                  toDateTime: row.toDateTime,
                }));
              }

              // Add sendUpdateEmail flag if there are changes
              if (hasPendingChanges) {
                payload.sendUpdateEmail = sendUpdateEmail;
              }

              setIsApplyingChanges(true);
              try {
                const updated = await apiService.updateAppointment(appointment.id, payload);
                if (onStatusChange) {
                  onStatusChange(updated);
                }

                // Reset local draft state to reflect what the server persisted
                const nextIso = getAppointmentStartIso(updated);
                const nextInitialSchedule = nextIso ? toLocalDateTimeInputValue(nextIso) : '';
                setInitialScheduledAtLocal(nextInitialSchedule);
                setDraftScheduledAtLocal(nextInitialSchedule);
                prevScheduleIsoRef.current = updated.scheduledAt ?? nextIso ?? null;
                activeAppointmentIdRef.current = String(updated.id);
                scheduleTouchedRef.current = false;

                const nextDraft: DraftAssignment[] = (updated.assignments || []).map((a: any) => ({
                  clientId: String(a.id),
                  id: String(a.id),
                  serviceId: String(a.serviceId),
                  serviceName: String(a.serviceName || ''),
                  durationMinutes: Number(a.durationMinutes || 0) || 0,
                  price: typeof a.price === 'number' ? a.price : Number(a.price || 0) || 0,
                  serviceQuotable: (a as any).serviceQuotable === true,
                  professionalId: a.professionalId ? String(a.professionalId) : undefined,
                  professionalFirstName: a.professionalFirstName,
                  professionalLastName: a.professionalLastName,
                  professionalEmail: a.professionalEmail,
                  notes: a.notes,
                  insistOnProfessional: a.insistOnProfessional === true,
                  fromDateTime: a.fromDateTime || undefined,
                  toDateTime: a.toDateTime || undefined,
                }));
                setDraftAssignments(nextDraft);
                setInitialAssignmentsKey(
                  JSON.stringify(
                    nextDraft.map((x) => ({
                      id: x.id || null,
                      serviceId: x.serviceId,
                      professionalId: x.professionalId || null,
                      fromDateTime: x.fromDateTime || null,
                      toDateTime: x.toDateTime || null,
                    }))
                  )
                );

                toast.success('Changes applied');
                setSendUpdateEmail(defaultSendUpdateEmail); // Reset checkbox
                onClose();
              } catch (error: any) {
                console.error('Error applying changes:', error);
                toast.error(error.response?.data?.message || 'Failed to apply changes');
              } finally {
                setIsApplyingChanges(false);
              }
            }}
            disabled={
              !hasPendingChanges ||
              (draftAssignments || []).length === 0 ||
              isApplyingChanges ||
              isUpdating ||
              appointment?.status === 'CANCELLED' ||
              appointment?.status === 'COMPLETED'
            }
            className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
          >
            {isApplyingChanges ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Applying...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Update</span>
              </>
            )}
          </button>
          </div>
        </div>
      </div>

      {/* Service Selection Modal (single-select; updates draft only) */}
      {isServicePickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Select Services</h2>
              </div>
              <button
                onClick={() => {
                  setIsServicePickerOpen(false);
                  setServicePickerClientId('');
                  setServicePickerSelected(null);
                  setServicePickerProfessionalId('');
                  servicePickerProfessionalTouchedRef.current = false;
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <BookingStep1
                // Exclude soft-deleted services from the picker list.
                services={(businessServices || []).filter((s: any) => s?.deleted !== true)}
                categories={serviceCategories as any}
                selectedServices={servicePickerSelected ? [servicePickerSelected] : []}
                onServicesChange={(next) => {
                  // Enforce single selection: keep 0..1 services
                  if (!next || next.length === 0) {
                    setServicePickerSelected(null);
                    return;
                  }
                  setServicePickerSelected(next[next.length - 1]);
                }}
                // Disable preselect logic; we set selection explicitly when opening the modal.
                preselectedServiceId={null}
                hideMobileCart={true}
                audience="professional"
                businessId={currentBusiness?.id || appointment?.businessId}
              />

              {servicePickerMode === 'add' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Professional</label>
                  <select
                    value={servicePickerProfessionalId}
                    onChange={(e) => {
                      servicePickerProfessionalTouchedRef.current = true;
                      setServicePickerProfessionalId(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">No assignment</option>
                    {professionals.map((p: any) => {
                      const proId = String(p.id);
                      const isConflict = conflictingProfessionalIds.has(proId);
                      const label = `${getProfessionalDisplayName(p)}${isConflict ? ' ⚠' : ''}`;
                      return (
                        <option key={p.id} value={p.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {loadingProfessionalConflicts && (
                    <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      Checking conflicts…
                    </div>
                  )}
                  {servicePickerProfessionalId && conflictingProfessionalIds.has(String(servicePickerProfessionalId)) && (
                    <div className="mt-2 text-xs text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      This professional appears to have a scheduling conflict at the selected time.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsServicePickerOpen(false);
                  setServicePickerClientId('');
                  setServicePickerSelected(null);
                  setServicePickerProfessionalId('');
                  servicePickerProfessionalTouchedRef.current = false;
                }}
                className="aacore-btn2-secondary flex-1 sm:flex-none"
              >
                <X className="w-4 h-4" />
                <span>Close</span>
              </button>
              <button
                onClick={() => {
                  if (!servicePickerSelected) return;
                  const svc: any = servicePickerSelected;
                  const nextServiceId = String(svc.id);
                  const nextName = String(svc.name || 'Service');
                  const nextDuration = Number(svc.durationMinutes ?? svc.duration_minutes ?? 0) || 0;
                  const nextPrice = Number(svc.price ?? 0) || 0;

                  if (servicePickerMode === 'add') {
                    const clientId = `new-${nextServiceId}-${Date.now()}`;
                    setDraftAssignments((prev) => {
                      const baseIso = fromLocalDateTimeInputToIso(draftScheduledAtLocal) || appointment?.scheduledAt;
                      const base = baseIso ? new Date(baseIso) : new Date();

                      const existingEnds = (prev || [])
                        .map((row) => (row.toDateTime ? new Date(row.toDateTime) : null))
                        .filter((d): d is Date => !!d && Number.isFinite(d.getTime()));

                      const fallbackOffsetMinutes = (prev || []).reduce((sum, row) => sum + (Number(row.durationMinutes) || 0), 0);
                      const start = existingEnds.length
                        ? new Date(Math.max(...existingEnds.map((d) => d.getTime())))
                        : new Date(base.getTime() + fallbackOffsetMinutes * 60 * 1000);

                      const fromDateTime = start.toISOString();
                      const toDateTime = new Date(start.getTime() + nextDuration * 60 * 1000).toISOString();

                      return [
                        ...prev,
                        {
                          clientId,
                          serviceId: nextServiceId,
                          serviceName: nextName,
                          durationMinutes: nextDuration,
                          price: nextPrice,
                          professionalId: servicePickerProfessionalId || undefined,
                          notes: '',
                          insistOnProfessional: false,
                          fromDateTime,
                          toDateTime,
                        },
                      ];
                    });
                  } else {
                    if (!servicePickerClientId) return;
                    setDraftAssignments((prev) =>
                      prev.map((row) =>
                        row.clientId === servicePickerClientId
                          ? {
                              ...row,
                              serviceId: nextServiceId,
                              serviceName: nextName,
                              durationMinutes: nextDuration,
                              price: nextPrice,
                              toDateTime: (() => {
                                const from = row.fromDateTime ? new Date(row.fromDateTime) : null;
                                if (!from || Number.isNaN(from.getTime())) return row.toDateTime;
                                return new Date(from.getTime() + nextDuration * 60 * 1000).toISOString();
                              })(),
                            }
                          : row
                      )
                    );
                  }

                  setIsServicePickerOpen(false);
                  setServicePickerClientId('');
                  setServicePickerSelected(null);
                  setServicePickerProfessionalId('');
                  servicePickerProfessionalTouchedRef.current = false;
                }}
                disabled={!servicePickerSelected}
                className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
              >
                {servicePickerMode === 'add' ? (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Update</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                {confirmationDialog.title === 'Confirm Appointment' && (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                )}
                {confirmationDialog.title === 'Cancel Appointment' && (
                  <Trash2 className="w-5 h-5 text-red-600" />
                )}
                {confirmationDialog.title === 'Mark as No Show' && (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                <span>{confirmationDialog.title}</span>
              </h2>
              <button
                onClick={() => {
                  setConfirmationDialog(null);
                  setCancellationNotes('');
                  setNoShow(false);
                  setSendCancellationEmail(false);
                  cancellationNotesRef.current = '';
                  noShowRef.current = false;
                  sendCancellationEmailRef.current = false;
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isUpdating}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex-1 min-h-0 overflow-y-auto">
              <p className="text-gray-700 mb-4">
                {confirmationDialog.message}
              </p>
              {confirmationDialog.depositWarning ? (
                <div
                  role="alert"
                  className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-3 items-start"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden />
                  <span>{confirmationDialog.depositWarning}</span>
                </div>
              ) : null}
              {confirmationDialog.showCancellationNotes && (
                <div className="mt-4 space-y-4">
                  {(() => {
                    const ap: any = appointment as any;
                    const hasSeriesId = Boolean(ap?.seriesId || ap?.series_id);
                    const isRecurring = seriesSummary?.isRecurring === true || hasSeriesId;
                    if (!isRecurring) return null;

                    const occurrenceIndex =
                      (typeof (seriesSummary as any)?.occurrenceIndex === 'number' ? (seriesSummary as any).occurrenceIndex : null) ??
                      (ap?.occurrenceIndex ?? ap?.occurrence_index ?? null);
                    const seriesCount =
                      (typeof (seriesSummary as any)?.seriesOccurrenceCount === 'number' ? (seriesSummary as any).seriesOccurrenceCount : null) ??
                      (ap?.seriesOccurrenceCount ?? ap?.series_occurrence_count ?? null);

                    const remainingAfterThis =
                      typeof seriesSummary?.remainingActiveAfterThis === 'number'
                        ? seriesSummary.remainingActiveAfterThis
                        : 0;
                    const showScopeChoice = Number.isFinite(remainingAfterThis) && remainingAfterThis > 0;

                    const completedLabel =
                      typeof seriesSummary?.completedCount === 'number' ? seriesSummary.completedCount : null;
                    const remainingLabel =
                      typeof seriesSummary?.remainingActiveIncludingThis === 'number' ? seriesSummary.remainingActiveIncludingThis : null;

                    return (
                      <div className="border border-blue-100 bg-blue-50/40 rounded-md p-4">
                        <p className="text-sm font-semibold text-gray-900">This appointment is part of a series</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {occurrenceIndex && seriesCount ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-200 bg-white text-xs font-semibold text-blue-900">
                              <ListOrdered className="w-3.5 h-3.5 text-blue-700" aria-hidden />
                              <span>
                                {Number(occurrenceIndex)} of {Number(seriesCount)}
                              </span>
                            </span>
                          ) : null}
                          {completedLabel != null ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                              <span>Completed: {completedLabel}</span>
                            </span>
                          ) : null}
                          {remainingLabel != null ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                              <span>Remaining: {remainingLabel}</span>
                            </span>
                          ) : null}
                        </div>

                        {isSeriesLoading ? (
                          <p className="mt-2 text-xs text-gray-600">Checking remaining appointments in this series…</p>
                        ) : null}

                        {showScopeChoice ? (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">What would you like to cancel?</p>
                            <div className="space-y-2">
                              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="radio"
                                  name="cancelScopeBusinessModal"
                                  value="THIS"
                                  checked={cancelScope === 'THIS'}
                                  onChange={() => {
                                    setCancelScope('THIS');
                                    cancelScopeRef.current = 'THIS';
                                  }}
                                  disabled={isUpdating}
                                  className="mt-1"
                                />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Cancel only this appointment</p>
                                  <p className="text-xs text-gray-600">All other upcoming appointments in the series will stay scheduled.</p>
                                </div>
                              </label>
                              <label className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-white hover:bg-red-50 cursor-pointer">
                                <input
                                  type="radio"
                                  name="cancelScopeBusinessModal"
                                  value="REMAINING"
                                  checked={cancelScope === 'REMAINING'}
                                  onChange={() => {
                                    setCancelScope('REMAINING');
                                    cancelScopeRef.current = 'REMAINING';
                                  }}
                                  disabled={isUpdating}
                                  className="mt-1"
                                />
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">Cancel this and all remaining appointments</p>
                                  <p className="text-xs text-gray-600">
                                    This will also cancel {remainingAfterThis} upcoming appointment{remainingAfterThis === 1 ? '' : 's'} in this series.
                                  </p>
                                </div>
                              </label>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}

                  {/* No Show Checkbox */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={noShow}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setNoShow(value);
                          noShowRef.current = value;
                        }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        disabled={isUpdating}
                      />
                      <span className="text-sm font-medium text-gray-900">No Show</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Check this if the customer didn't show up for the appointment
                    </p>
                  </div>
                  
                  {/* Cancellation Reason */}
                  <div>
                    <label htmlFor="cancellation-notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Cancellation Reason (Optional)
                    </label>
                    <textarea
                      id="cancellation-notes"
                      value={cancellationNotes}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCancellationNotes(value);
                        cancellationNotesRef.current = value;
                      }}
                      placeholder="Enter the reason for cancellation..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                      disabled={isUpdating}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      This information will be saved with the appointment record.
                    </p>
                  </div>

                  {/* Send cancellation email to customer */}
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendCancellationEmail}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setSendCancellationEmail(value);
                          sendCancellationEmailRef.current = value;
                        }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        disabled={isUpdating}
                      />
                      <span className="text-sm font-medium text-gray-900">Send cancellation email to customer</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Check this to notify the customer by email; leave unchecked to cancel without sending an email.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 shrink-0">
              <button
                onClick={() => {
                  setConfirmationDialog(null);
                  setCancellationNotes('');
                  setNoShow(false);
                  setSendCancellationEmail(false);
                  setCancelScope('THIS');
                  cancelScopeRef.current = 'THIS';
                  setSeriesSummary(null);
                  cancellationNotesRef.current = '';
                  noShowRef.current = false;
                  sendCancellationEmailRef.current = false;
                }}
                disabled={isUpdating}
                className="aacore-btn2-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{confirmationDialog.cancelText}</span>
              </button>
              <button
                onClick={confirmationDialog.onConfirm}
                disabled={isUpdating}
                className={`disabled:opacity-50 disabled:cursor-not-allowed ${
                  confirmationDialog.confirmButtonClass?.includes('red') ? 'aacore-btn2-danger' : 
                  confirmationDialog.confirmButtonClass?.includes('green') ? 'aacore-btn2-success' : 
                  'aacore-btn2-primary'
                }`}
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {confirmationDialog.title === 'Confirm Appointment' && (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {confirmationDialog.title === 'Cancel Appointment' && (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {confirmationDialog.title === 'Remove Service' && (
                      <Trash2 className="w-4 h-4" />
                    )}
                    {confirmationDialog.title === 'Mark as No Show' && (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    <span>{confirmationDialog.confirmText}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <MarkDepositPaidModal
        isOpen={isMarkDepositPaidModalOpen}
        depositAmount={getDepositAmountToRecord(fetchedAppointmentDetail ?? appointment)}
        onClose={() => setIsMarkDepositPaidModalOpen(false)}
        onSubmit={handleMarkDepositPaidSubmit}
      />

      {/* Edit Duration Modal */}
      {isDurationModalOpen && durationEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0B4778]/10">
                  <CalendarClock className="w-4 h-4 text-[#0B4778]" />
                </div>
                <span>Edit Duration</span>
              </h2>
              <button
                onClick={closeDurationModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isSavingDuration}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{durationEditing.serviceName}</div>
                <div className="text-gray-600 mt-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Starts {new Date(durationEditing.startIso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                    {' • '}
                    Ends{' '}
                    {(() => {
                      const h = Number(durationHours) || 0;
                      const m = Number(durationMinutes) || 0;
                      const total = h * 60 + m;
                      const end = new Date(new Date(durationEditing.startIso).getTime() + total * 60 * 1000);
                      return end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    })()}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                  <select
                    value={durationHours}
                    onChange={(e) => setDurationHours(e.target.value)}
                    disabled={isSavingDuration}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                  >
                    {Array.from({ length: 13 }, (_, i) => String(i)).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                  <select
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    disabled={isSavingDuration}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                  >
                    {['00', '15', '30', '45'].map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Duration changes are saved immediately and will update the assignment end time.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeDurationModal}
                disabled={isSavingDuration}
                className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!appointment || !durationEditing) return;
                  const h = Number(durationHours) || 0;
                  const m = Number(durationMinutes) || 0;
                  const total = h * 60 + m;
                  if (total < 15) {
                    toast.error('Duration must be at least 15 minutes');
                    return;
                  }
                  if (total % 15 !== 0) {
                    toast.error('Minutes must be 0, 15, 30, or 45');
                    return;
                  }
                  setIsSavingDuration(true);
                  try {
                    await apiService.updateAssignmentDuration({
                      appointmentId: appointment.id,
                      assignmentId: durationEditing.assignmentId,
                      durationMinutes: total
                    });
                    const updated = await apiService.getAppointment(appointment.id);
                    if (onStatusChange) onStatusChange(updated);

                    // Keep modal UI in sync without forcing the user to close/reopen.
                    const nextDraft: DraftAssignment[] = (updated.assignments || []).map((as: any) => ({
                      clientId: String(as.id),
                      id: String(as.id),
                      serviceId: String(as.serviceId),
                      serviceName: String(as.serviceName || ''),
                      durationMinutes: Number(as.durationMinutes || 0) || 0,
                      price: typeof as.price === 'number' ? as.price : Number(as.price || 0) || 0,
                      serviceQuotable: (as as any).serviceQuotable === true,
                      professionalId: as.professionalId ? String(as.professionalId) : undefined,
                      professionalFirstName: as.professionalFirstName,
                      professionalLastName: as.professionalLastName,
                      professionalEmail: as.professionalEmail,
                      notes: as.notes,
                      insistOnProfessional: as.insistOnProfessional === true,
                      fromDateTime: as.fromDateTime || undefined,
                      toDateTime: as.toDateTime || undefined,
                    }));
                    setDraftAssignments(nextDraft);
                    setInitialAssignmentsKey(
                      JSON.stringify(
                        nextDraft.map((x) => ({
                          id: x.id || null,
                          serviceId: x.serviceId,
                          professionalId: x.professionalId || null,
                          fromDateTime: x.fromDateTime || null,
                          toDateTime: x.toDateTime || null,
                        }))
                      )
                    );

                    toast.success('Duration updated');
                    closeDurationModal();
                  } catch (error: any) {
                    console.error('Error updating duration:', error);
                    toast.error(error.response?.data?.message || 'Failed to update duration');
                  } finally {
                    setIsSavingDuration(false);
                  }
                }}
                disabled={isSavingDuration}
                className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingDuration ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCustomerAppointmentsModalOpen && currentBusiness?.id && historyCustomerId && (
        <CustomerAppointmentsHistoryModal
          isOpen={isCustomerAppointmentsModalOpen}
          onClose={() => setIsCustomerAppointmentsModalOpen(false)}
          businessId={currentBusiness.id}
          customerId={historyCustomerId}
          customerName={historyCustomerName}
          currentAppointmentId={appointment.id}
        />
      )}

      {isSeriesListModalOpen && appointment && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[90] flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setIsSeriesListModalOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[min(85vh,560px)] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="appointment-modal-series-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <h2
                id="appointment-modal-series-list-title"
                className="text-lg font-semibold text-gray-900 flex items-center gap-2"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0B4778]/10">
                  <Repeat2 className="w-5 h-5 text-[#0B4778]" />
                </div>
                <span>Series Appointments</span>
              </h2>
              <button
                type="button"
                onClick={() => setIsSeriesListModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {seriesListLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : seriesListItems.length === 0 ? (
                <p className="text-sm text-gray-600 text-center py-8">No other appointments found in this series.</p>
              ) : (
                <ul className="space-y-2">
                  {seriesListItems.map((row) => {
                    const isCurrent = String(row.id) === String(appointment.id);
                    const whenLabel = row.scheduledAt
                      ? `${formatDateOnly(row.scheduledAt)} · ${formatTimeOnly(row.scheduledAt)}`
                      : row.flexScheduledAt?.earliest
                        ? `${formatDateOnly(row.flexScheduledAt.earliest)} · ${formatTimeOnly(row.flexScheduledAt.earliest)} – ${formatTimeOnly(row.flexScheduledAt.latest)}`
                        : '—';
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={async () => {
                            setIsSeriesListModalOpen(false);
                            if (String(row.id) === String(appointment.id)) return;
                            try {
                              const full = await apiService.getAppointment(row.id);
                              if (onNavigateToAppointment) {
                                onNavigateToAppointment(full);
                              } else {
                                onClose();
                                navigate(`/appointments/${row.id}${location.search || ''}`);
                              }
                            } catch (err: any) {
                              console.error('Error loading appointment:', err);
                              toast.error(err?.response?.data?.message || 'Could not open that appointment');
                            }
                          }}
                          className={`w-full text-left rounded-lg border px-3 py-3 transition-colors ${
                            isCurrent
                              ? 'border-[#0B4778] bg-[#0B4778]/5 ring-1 ring-[#0B4778]/30'
                              : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {row.occurrenceIndex != null ? `Visit ${row.occurrenceIndex}` : 'Visit'}
                            </span>
                            <span
                              className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                                row.status === 'CONFIRMED'
                                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                                  : row.status === 'PENDING' || row.status === 'PENDING_QUOTE'
                                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                                    : row.status === 'COMPLETED'
                                      ? 'bg-green-50 text-green-800 border-green-200'
                                      : row.status === 'CANCELLED'
                                        ? 'bg-red-50 text-red-800 border-red-200'
                                        : 'bg-gray-50 text-gray-700 border-gray-200'
                              }`}
                            >
                              {formatStatus(row.status)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{whenLabel}</div>
                          {isCurrent && (
                            <div className="text-xs text-[#0B4778] font-medium mt-1">Current appointment</div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsSeriesListModalOpen(false)}
                className="aacore-btn2-secondary"
              >
                <X className="w-4 h-4" />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetailsModal; 